import { FC, useEffect, useRef, useState } from 'react';
import FavoritesButton from '@/components/common/layout/header-toolbar/favorites-button';
import MyCartButton from '@/components/common/layout/header-toolbar/my-cart-button';
import UserAuthToolbarSlot from '@/components/common/layout/user-button/user-auth-toolbar-slot';
import { Link } from '@/components/ui';
import { RenderableImage } from '@/components/ui/image/renderable-image';
import useCoreConfig from '@/hooks/use-core-config';
import useHeaderContent from '@/hooks/use-header-content';
import { useResolveThemeColor } from '@/hooks/use-resolve-theme-color';
import { useScrollYForHeaderLayout } from '@/hooks/use-scroll-y-for-header-layout';
import { useAppContext } from '@/providers/app-provider/app-provider';
import { useKeycloak } from '@/providers/keycloak-provider';
import { cn } from '@/utils';
import FeatureFlag from '../../feature-flag/feature-flag';
import ContactBanner from '../contact-banner/contact-banner';
import DeferredDesktopGlobalSearch from './deferred-desktop-global-search';
import LanguageSelector from './language-selector/language-selector';
import MobileMenu from './mobile-menu/mobile-menu';
import MobileSearch from './mobile-search/mobile-search';
import NavigationBarV1 from './navigation-bar/navigation-bar-v1';

const AppBar: FC = () => {
  const resolveThemeColor = useResolveThemeColor();
  const { isStudioPreview } = useAppContext<{ isStudioPreview?: boolean }>();
  const stickyRef = useRef<HTMLDivElement>(null);
  const { isAuthenticated } = useKeycloak();
  const { features } = useCoreConfig();
  const scrollY = useScrollYForHeaderLayout(isStudioPreview, stickyRef);
  const { logoSrc } = useCoreConfig();

  // Defaults to `true` so SSR / first hydration render matches the closed
  // modal state (see `age-verification-modal.tsx`); the real value is read
  // in the effect below. Touching `sessionStorage` synchronously during
  // render would crash on the server.
  const [isAgeVerified, setIsAgeVerified] = useState(true);

  useEffect(() => {
    setIsAgeVerified(sessionStorage.getItem('isAgeVerified') !== null);
  }, []);

  const headerContent = useHeaderContent();
  const parsedLogoHeight = Number(headerContent.logoHeight);
  const logoHeight =
    Number.isFinite(parsedLogoHeight) && parsedLogoHeight > 0 ? parsedLogoHeight : 40;
  const resolvedLogoSrc = headerContent.logoSrc || logoSrc || '/imgs/ibee-logo.png';

  return (
    <div
      ref={stickyRef}
      className={cn(
        'sticky top-0 left-0 w-full transition-shadow duration-300',
        scrollY > 30 ? 'shadow-md' : 'shadow-none',
        isAgeVerified ? 'z-50' : 'z-40',
      )}
    >
      <div className="flex flex-col">
        {headerContent.contactBanner && <ContactBanner />}
        <div className="flex flex-col">
          <div
            style={{
              background: resolveThemeColor(
                headerContent.mainBackground ?? headerContent.background ?? 'content1',
              ),
            }}
          >
            <div className="container mx-auto flex w-full items-center px-5 py-1">
              <div className="flex shrink-0 items-center gap-1">
                <MobileMenu />

                <div
                  className={cn(
                    'transform-origin-left transition-transform duration-300',
                    scrollY > 30 ? 'scale-85' : 'scale-100',
                  )}
                >
                  <Link href="/">
                    <div className="flex items-center" style={{ height: `${logoHeight}px` }}>
                      <RenderableImage
                        src={resolvedLogoSrc}
                        height={logoHeight}
                        className="block h-full w-auto max-w-[220px] cursor-pointer object-contain"
                        alt="logo"
                      />
                    </div>
                  </Link>
                </div>
              </div>

              <FeatureFlag feature="globalSearch">
                <DeferredDesktopGlobalSearch className="mr-24 ml-20 hidden grow rounded-full border-none bg-white shadow-none @lg:block" />
              </FeatureFlag>

              <div className="ml-auto flex shrink-0 items-center gap-0 @sm:gap-0.5 @lg:gap-0.5">
                <div className="hidden @lg:block">
                  <LanguageSelector />
                </div>
                <FeatureFlag feature="favorites">
                  <FavoritesButton />
                </FeatureFlag>
                {(isAuthenticated || features.cartForGuests) && <MyCartButton />}
                <UserAuthToolbarSlot variant="primary" />
              </div>
            </div>
          </div>

          <div
            style={{
              background: resolveThemeColor(
                headerContent.background ?? headerContent.mainBackground ?? 'content1',
              ),
            }}
          >
            <div className="container mx-auto px-5">
              <div className="flex justify-start">
                <NavigationBarV1 />
              </div>
            </div>
          </div>
        </div>

        <FeatureFlag feature="globalSearch">
          <MobileSearch />
        </FeatureFlag>
      </div>
    </div>
  );
};

export default AppBar;
